import { ErrorCode } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { FORM_ERROR } from 'final-form';
import { isEmpty } from 'lodash';
import React from 'react';

import type { RequestError } from '@swf/core/errors/RequestError';
import type { Response } from '@swf/core/types/Account';
import { ServerMessageHandler } from '@swf/notification';

import type { ErrorCollection } from '../types/Response';

import {
  TXTACL_Internet_Connection_Lost,
  TXTWCL_Sorry___an_error_has_occurred
} from './forms.messages.json';

interface Errors {
  [key: string]: React.ReactNode;
}

export interface FormResponse {
  success: boolean;
  message: string;
  errors: { fieldErrors: { fieldName: string; code: string }[] };
}

export const buildFormErrors = (
  response: Response,
  errorTranslations: Record<string, React.ComponentProps<typeof Translation>> = {}
): Optional<Errors> => {
  const errors: Record<string, React.ReactElement | (string | null)> = {};
  if (!response.success) {
    if (response.message) {
      errors[FORM_ERROR] = (
        <ServerMessageHandler message={response.message} code={response.statusCode} />
      );
    } else if (response.messages) {
      errors[FORM_ERROR] = (
        <ServerMessageHandler message={response.messages.msg.error} code={response.statusCode} />
      );
    } else if (response.errors?.code === ErrorCode.CLIENT_TIMEOUT) {
      errors[FORM_ERROR] = <Translation {...TXTACL_Internet_Connection_Lost} />;
    } else {
      errors[FORM_ERROR] = <Translation {...TXTWCL_Sorry___an_error_has_occurred} />;
    }
    if (response.errors?.fieldErrors) {
      response.errors.fieldErrors.forEach(({ fieldName, code }) => {
        const TRANSLATION =
          errorTranslations[`${code}.${fieldName}`] || errorTranslations[code || ''];
        errors[fieldName] = TRANSLATION ? <Translation {...TRANSLATION} /> : code;
      });
    }
  }

  return isEmpty(errors) ? undefined : errors;
};

export const buildFormErrorsFromGenericStatus = (
  error: RequestError<ErrorCollection>,
  errorTranslations?: Record<string, React.ComponentProps<typeof Translation>>
): Optional<Errors> => {
  const response = {
    statusCode: error.headers?.statusCode,
    success: false,
    message: isEmpty(error.body) ? error.message : error.body.message,
    errors: error.body
  };

  return buildFormErrors(response, errorTranslations);
};
